import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLazyExportFleetReportByIdQuery } from "../../../../../redux/api/generation/generationAPI";
import { EDateTypes } from "../../../../../shared/oversight-core/enums/date-types";
import { EExportFleetReportFormat } from "../../../../../shared/oversight-core/enums/export-fleet-report-format";
import AppDatePicker from "../../../../../shared/oversight-core/ui-elements/app-date-picker/app-date-picker";
import AppSelect, {
  Option,
} from "../../../../../shared/oversight-core/ui-elements/app-select/app-select";
import ModalContainer from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  getDefaultDates,
  getMaxFromDate,
  getMinToDate,
  isSameOrAfterRange,
  isSameOrBeforeRange,
} from "../../../../../shared/oversight-core/utils/date-utils";
import { downloadFile } from "../../../../../shared/oversight-core/utils/download-file";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";
import styles from "./export-site-report-modal.module.scss";

interface IProps {
  show: boolean;
  onClose: () => void;
  plantId: string;
}

const formatOptions: Option[] = [
  { value: EExportFleetReportFormat.XLSX, label: "XLSX" },
  { value: EExportFleetReportFormat.CSV, label: "CSV" },
  { value: EExportFleetReportFormat.PDF, label: "PDF" },
];

const ExportSiteReportModal = (props: IProps) => {
  const { show, onClose, plantId } = props;

  const [selectedDateType, setSelectedDateType] = useState<EDateTypes>(
    EDateTypes.DAILY,
  );
  const [startDate, setStartDate] = useState(
    () => getDefaultDates(EDateTypes.DAILY).start,
  );
  const [endDate, setEndDate] = useState(
    () => getDefaultDates(EDateTypes.DAILY).end,
  );

  const handleStartDateChange = (newStartDate: Date) => {
    setStartDate(newStartDate);
    if (isSameOrAfterRange(newStartDate, endDate, selectedDateType)) {
      setEndDate(getMinToDate(newStartDate, selectedDateType));
    }
  };

  const handleEndDateChange = (newEndDate: Date) => {
    setEndDate(newEndDate);
    if (isSameOrBeforeRange(newEndDate, startDate, selectedDateType)) {
      setStartDate(getMaxFromDate(newEndDate, selectedDateType));
    }
  };

  const [dateFormat, setDateFormat] = useState("dd-MMM-yyyy");

  const [selectedFormat, setSelectedFormat] = useState<Option>({
    value: EExportFleetReportFormat.XLSX,
    label: "XLSX",
  });

  const [triggerExport, { isFetching: isExporting }] =
    useLazyExportFleetReportByIdQuery();

  useEffect(() => {
    if (show) {
      const defaultDates = getDefaultDates(EDateTypes.DAILY);
      setStartDate(defaultDates.start);
      setEndDate(defaultDates.end);
      setSelectedDateType(EDateTypes.DAILY);
      setSelectedFormat({
        value: EExportFleetReportFormat.XLSX,
        label: "XLSX",
      });
    }
  }, [show]);

  useEffect(() => {
    switch (selectedDateType) {
      case EDateTypes.DAILY:
        setDateFormat("dd-MMM-yyyy");
        break;
      case EDateTypes.MONTHLY:
        setDateFormat("MMMM-yyyy");
        break;
      case EDateTypes.YEARLY:
        setDateFormat("yyyy");
        break;
      default:
        setDateFormat("dd-MMM-yyyy");
        break;
    }
  }, [selectedDateType]);

  const handleExport = () => {
    triggerExport({
      plantId,
      startDate: moment
        .utc([
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
        ])
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm:ss[Z]"),
      endDate: moment
        .utc([endDate.getFullYear(), endDate.getMonth(), endDate.getDate()])
        .endOf("day")
        .format("YYYY-MM-DDTHH:mm:ss[Z]"),
      viewType: selectedDateType,
      format: selectedFormat.value as EExportFleetReportFormat,
    })
      .unwrap()
      .then((blob) => {
        const fileExtension = selectedFormat.value.toLowerCase();
        downloadFile(
          blob,
          `report-${moment().format("YYYYMMDDHHmmss")}.${fileExtension}`,
        );
        showSuccessMessage("Report exported successfully");
        onClose();
      })
      .catch((error) => {
        showErrorMessage(error?.data?.message || "Failed to export report");
      });
  };

  const dateTypes = [EDateTypes.DAILY, EDateTypes.MONTHLY, EDateTypes.YEARLY];

  return (
    <ModalContainer
      show={show}
      title="Export Report"
      size="modal-md"
      confirmButtonText="Export"
      cancelButtonText="Cancel"
      onConfirm={handleExport}
      onCancel={onClose}
      onClose={onClose}
      isLoading={isExporting}
    >
      <div className="mt-2">
        <label className="font-size-14 text-light">Time</label>
        <Row className="justify-content-between px-3">
          {dateTypes.map((type, index) => (
            <Col
              key={index}
              onClick={() => {
                if (selectedDateType !== type) {
                  setSelectedDateType(type);
                  const defaultDates = getDefaultDates(type);
                  setStartDate(defaultDates.start);
                  setEndDate(defaultDates.end);
                }
              }}
              className={`col-4 mt-2 font-weight-500 font-size-12 py-2 cursor-pointer text-center ${
                selectedDateType === type ? styles.selected : styles.button
              }`}
            >
              {type}
            </Col>
          ))}
        </Row>
        <Row className="mt-4">
          <Col xs={6}>
            <Row className="flex-column">
              <Col>
                <label className="font-size-14 text-light">From</label>
              </Col>
              <Col>
                <AppDatePicker
                  selectedDate={startDate}
                  onChange={handleStartDateChange}
                  monthYearPicker={selectedDateType === EDateTypes.MONTHLY}
                  yearPicker={selectedDateType === EDateTypes.YEARLY}
                  dateFormat={dateFormat}
                  fontSize={12}
                  maxDate={getMaxFromDate(endDate, selectedDateType)}
                />
              </Col>
            </Row>
          </Col>
          <Col xs={6}>
            <Row className="flex-column">
              <Col>
                <label className="font-size-14 text-light">To</label>
              </Col>
              <Col>
                <AppDatePicker
                  selectedDate={endDate}
                  onChange={handleEndDateChange}
                  monthYearPicker={selectedDateType === EDateTypes.MONTHLY}
                  yearPicker={selectedDateType === EDateTypes.YEARLY}
                  dateFormat={dateFormat}
                  fontSize={12}
                  minDate={getMinToDate(startDate, selectedDateType)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={12}>
            <AppSelect
              className="text-light font-size-14"
              selectedValue={selectedFormat}
              label="Format"
              options={formatOptions}
              onChangeOption={(selectedOption) =>
                setSelectedFormat(selectedOption)
              }
              isRequired={false}
              labelFontSize="font-size-14"
            />
          </Col>
        </Row>
      </div>
    </ModalContainer>
  );
};

export default ExportSiteReportModal;
